import Helper from '@ember/component/helper';
import Service, { inject as service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import { assert } from '@ember/debug';
const unitToTranslationKeysMap = {
  // Length units
  IMPERIAL_INCH: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.length.imperialInch',
  IMPERIAL_FOOT: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.length.imperialFoot',
  IMPERIAL_YARD: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.length.imperialYard',
  IMPERIAL_MILE: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.length.imperialMile',
  METRIC_MILLIMETER: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.length.metricMillimeter',
  METRIC_CENTIMETER: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.length.metricCentimeter',
  METRIC_METER: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.length.metricMeter',
  METRIC_KILOMETER: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.length.metricKilometer',
  // Area units
  IMPERIAL_ACRE: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.area.imperialAcre',
  IMPERIAL_SQUARE_INCH: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.area.imperialSquareInch',
  IMPERIAL_SQUARE_FOOT: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.area.imperialSquareFoot',
  IMPERIAL_SQUARE_YARD: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.area.imperialSquareYard',
  IMPERIAL_SQUARE_MILE: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.area.imperialSquareMile',
  METRIC_SQUARE_CENTIMETER: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.area.metricSquareCentimeter',
  METRIC_SQUARE_METER: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.area.metricSquareMeter',
  METRIC_SQUARE_KILOMETER: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.area.metricSquareKilometer',
  // Volume units
  GENERIC_FLUID_OUNCE: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.volume.genericFluidOunce',
  GENERIC_PINT: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.volume.genericPint',
  GENERIC_QUART: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.volume.genericQuart',
  GENERIC_GALLON: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.volume.genericGallon',
  GENERIC_SHOT: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.volume.genericShot',
  GENERIC_CUP: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.volume.genericCup',
  IMPERIAL_CUBIC_INCH: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.volume.imperialCubicInch',
  IMPERIAL_CUBIC_FOOT: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.volume.imperialCubicFoot',
  IMPERIAL_CUBIC_YARD: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.volume.imperialCubicYard',
  METRIC_MILLILITER: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.volume.metricMilliliter',
  METRIC_LITER: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.volume.metricLiter',
  // Weight units
  IMPERIAL_WEIGHT_OUNCE: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.weight.imperialWeightOunce',
  METRIC_MILLIGRAM: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.weight.metricMilligram',
  METRIC_GRAM: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.weight.metricGram',
  METRIC_KILOGRAM: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.weight.metricKilogram',
  IMPERIAL_POUND: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.weight.imperialPound',
  IMPERIAL_STONE: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.weight.imperialStone',
  // Time Unit:
  GENERIC_MILLISECOND: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.time.genericMillisecond',
  GENERIC_SECOND: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.time.genericSecond',
  GENERIC_MINUTE: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.time.genericMinute',
  GENERIC_HOUR: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.time.genericHour',
  GENERIC_DAY: 'items.itemLibrary.catalogMeasurementUnitAbbreviation.time.genericDay'
};
export function getUnitAbbreviationTranslation(unitName, serviceOrT) {
  if (serviceOrT instanceof Service) {
    return serviceOrT.t(unitToTranslationKeysMap[unitName]);
  } else {
    return serviceOrT(unitToTranslationKeysMap[unitName]);
  }
}

// second parameter can either be intl service or the t function provided by intl service.
export function formatMeasurementUnitAbbreviation(measurementUnitArg, intlOrT) {
  const measurementUnit = measurementUnitArg?.measurementUnitData?.measurementUnit || measurementUnitArg;
  if (isEmpty(measurementUnit)) {
    return '';
  }
  if (isPresent(measurementUnit.customUnit)) {
    return measurementUnit.customUnit.abbreviation;
  }
  if (isPresent(measurementUnit.areaUnit)) {
    return getUnitAbbreviationTranslation(measurementUnit.areaUnit, intlOrT);
  }
  if (isPresent(measurementUnit.lengthUnit)) {
    return getUnitAbbreviationTranslation(measurementUnit.lengthUnit, intlOrT);
  }
  if (isPresent(measurementUnit.weightUnit)) {
    return getUnitAbbreviationTranslation(measurementUnit.weightUnit, intlOrT);
  }
  if (isPresent(measurementUnit.volumeUnit)) {
    return getUnitAbbreviationTranslation(measurementUnit.volumeUnit, intlOrT);
  }
  if (isPresent(measurementUnit.timeUnit)) {
    return getUnitAbbreviationTranslation(measurementUnit.timeUnit, intlOrT);
  }
  if (isPresent(measurementUnit.genericUnit)) {
    return '';
  }
  assert('Should have found a translation for unit abbreviation');
  return '';
}

// eslint-disable-next-line ember/no-classic-classes
export default Helper.extend({
  intl: service(),
  compute([measurementUnit]) {
    return formatMeasurementUnitAbbreviation(measurementUnit, this.intl);
  }
});